import { FunctionComponent, useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styles from './NumericInput.module.css';

interface NumericInputProps {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  placeholder?: string;
  required?: boolean;
  showControls?: boolean;
  showRange?: boolean;
  normalRange?: {
    min: number;
    max: number;
    label?: string;
  };
  helperText?: string;
  hasNextButton?: boolean;
  onNext?: () => void;
  error?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'inline' | 'bordered';
}

const NumericInput: FunctionComponent<NumericInputProps> = ({
  label,
  value,
  onChange,
  unit,
  min,
  max,
  step = 1,
  precision = 0,
  placeholder,
  required = false,
  showControls = false,
  showRange = false,
  normalRange,
  helperText,
  hasNextButton,
  onNext,
  error,
  size = 'medium',
  variant = 'default'
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value.toFixed(precision));
    } else {
      setInputValue('');
    }
  }, [value, precision]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (newValue === '') {
      onChange(undefined);
      return;
    }

    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      if (min !== undefined && numValue < min) return;
      if (max !== undefined && numValue > max) return;
      onChange(numValue);
    }
  };

  const handleIncrement = () => {
    const currentValue = value ?? 0;
    const newValue = currentValue + step;
    if (max === undefined || newValue <= max) {
      onChange(newValue);
    }
  };

  const handleDecrement = () => {
    const currentValue = value ?? 0;
    const newValue = currentValue - step;
    if (min === undefined || newValue >= min) {
      onChange(newValue);
    }
  };

  const isInNormalRange = () => {
    if (!normalRange || value === undefined) return true;
    return value >= normalRange.min && value <= normalRange.max;
  };

  const getProgressPercentage = () => {
    if (min === undefined || max === undefined || value === undefined) return 0;
    return ((value - min) / (max - min)) * 100;
  };

  const containerClasses = `${styles.container} ${styles[size]} ${styles[variant]}`;
  const inputClasses = `${styles.input} ${
    error ? styles.error : ''
  } ${!isInNormalRange() ? styles.warning : ''}`;

  return (
    <div className={containerClasses}>
      {variant !== 'inline' && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputWrapper}>
        {showControls && (
          <button
            type="button"
            className={styles.controlButton}
            onClick={handleDecrement}
            disabled={min !== undefined && (value ?? 0) <= min}
            aria-label="Decrease value"
          >
            <RemoveIcon />
          </button>
        )}

        <div className={styles.inputContainer}>
          {variant === 'inline' && (
            <span className={styles.inlineLabel}>{label}</span>
          )}
          
          <input
            type="number"
            className={inputClasses}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            required={required}
          />
          
          {unit && <span className={styles.unit}>{unit}</span>}
        </div>

        {showControls && (
          <button
            type="button"
            className={styles.controlButton}
            onClick={handleIncrement}
            disabled={max !== undefined && (value ?? 0) >= max}
            aria-label="Increase value"
          >
            <AddIcon />
          </button>
        )}
      </div>

      {showRange && min !== undefined && max !== undefined && (
        <div className={styles.rangeContainer}>
          <div className={styles.rangeTrack}>
            <div 
              className={styles.rangeProgress} 
              style={{ width: `${getProgressPercentage()}%` }}
            />
            {value !== undefined && (
              <div 
                className={styles.rangeThumb} 
                style={{ left: `${getProgressPercentage()}%` }}
              />
            )}
          </div>
          <div className={styles.rangeLabels}>
            <span>{min}{unit && ` ${unit}`}</span>
            <span>{max}{unit && ` ${unit}`}</span>
          </div>
        </div>
      )}

      {normalRange && !isInNormalRange() && (
        <div className={styles.normalRangeWarning}>
          <InfoIcon className={styles.warningIcon} />
          <span>
            {normalRange.label || 'Normal range'}: {normalRange.min}-{normalRange.max}{unit && ` ${unit}`}
          </span>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>
          <span>{error}</span>
        </div>
      )}

      {helperText && !error && (
        <div className={styles.helperText}>
          <span>{helperText}</span>
        </div>
      )}

      {hasNextButton && (
        <button 
          type="button" 
          className={styles.nextButton} 
          onClick={onNext}
          disabled={required && value === undefined}
        >
          Next
          <ChevronRightIcon />
        </button>
      )}
    </div>
  );
};

// Common unit presets
export const units = {
  weight: {
    imperial: 'lbs',
    metric: 'kg'
  },
  height: {
    imperial: 'ft',
    metric: 'cm'
  },
  temperature: {
    imperial: '°F',
    metric: '°C'
  },
  distance: {
    imperial: 'miles',
    metric: 'km'
  },
  volume: {
    imperial: 'oz',
    metric: 'ml'
  },
  medication: {
    solid: 'mg',
    liquid: 'ml',
    units: 'units'
  }
};

// Common ranges
export const commonRanges = {
  age: { min: 0, max: 120 },
  weight: {
    lbs: { min: 0, max: 500 },
    kg: { min: 0, max: 227 }
  },
  height: {
    ft: { min: 0, max: 9 },
    cm: { min: 0, max: 275 }
  },
  bloodSugar: {
    fasting: { min: 70, max: 100, label: 'Normal fasting' },
    postMeal: { min: 70, max: 140, label: 'Normal after meal' }
  },
  cholesterol: {
    total: { min: 0, max: 200, label: 'Desirable' },
    ldl: { min: 0, max: 100, label: 'Optimal' },
    hdl: { min: 40, max: 200, label: 'Normal' }
  }
};

export default NumericInput;